import React, {
  FC,
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useSetFocus } from '@please/lrud'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { AutoHide } from '@/components/AutoHide/AutoHide'
import { ProgressBar } from '@/components/ProgressBar/ProgressBar'
import { VideoHeading } from '@/components/VideoHeading/VideoHeading'
import {
  AnimationBox,
  Box,
  ControlsContainer,
  FocusButtonNode,
  PlayImage,
  StyledControlsContainer,
} from '@/components/VideoPlayer/styles'
import {
  eventObject,
  eventAction,
  videoPlayerNames,
  analyticsVideoTypes,
} from '@/constants/analytics'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { largeArticleMock } from '@/constants/mock'
import { BodyText } from '@/containers/styles'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import useStore from '@/store/useStore'
import { AnalyticsVideoDataType } from '@/types/analyticsTypes'
import { getEpgObject, sendAnalyticsEvent } from '@/utils/analyticsUtils'

import { useRewindYouTubeVideo } from './useRewindYouTubeVideo'
import { PlayerState, YouTubePlayerType } from './YouTubePlayer'

export type YouTubeControlsProps = {
  videoElementRef: RefObject<YouTubePlayerType>
  playing: boolean
  title?: string
  description?: string
  videoId: string
  videoName?: string
  isVideoStarted: boolean
  videoUrl: string
}

const YouTubeControlsRaw: FC<YouTubeControlsProps> = ({
  videoElementRef,
  playing,
  title = '',
  description = '',
  videoId,
  videoName,
  isVideoStarted,
  videoUrl,
}) => {
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const { videoControlsVisible, showVideoControls, hideVideoControls } =
    useStore((state) => state.player)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const setYouTubeVideo = useStore((state) => state.common.setYouTubeVideo)

  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [pauseTimeUpdate, setPauseTimeUpdate] = useState(false)

  const { seekBack, seekForward } = useRewindYouTubeVideo(
    videoElementRef,
    currentTime,
    duration,
    setCurrentTime,
    setPauseTimeUpdate,
  )

  useEffect(() => {
    setFocus('section_1')
  }, [videoControlsVisible])

  useEffect(() => {
    if (pauseTimeUpdate) {
      return
    }
    const currentTimeInterval = setInterval(() => {
      const {
        duration = 0,
        currentTime = 0,
        playerState,
      } = videoElementRef.current?.playerInfo || {}

      if (playerState !== PlayerState.PLAYING) {
        return
      }

      setDuration(duration)
      setCurrentTime(currentTime)
    }, 1000)

    return () => {
      clearInterval(currentTimeInterval)
    }
  }, [videoElementRef, pauseTimeUpdate])

  const handleVideoAnalyticsEvent = useCallback(
    (action: string) => {
      const video: AnalyticsVideoDataType = {
        id: videoId,
        link: videoUrl,
      }

      if (action === eventAction.START_CLICK) {
        const epg = currentEvent ? getEpgObject(currentEvent) : undefined
        video.name = videoName
        video.playerName = videoPlayerNames.YOUTUBE
        video.type = currentEvent?.live
          ? analyticsVideoTypes.STREAM
          : analyticsVideoTypes.VOD
        video.length = currentEvent?.live
          ? '0'
          : videoElementRef?.current?.playerInfo?.duration
        video.epg = epg
      }

      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action,
        video,
      })
    },
    [videoId, videoUrl, currentEvent],
  )

  const play = useCallback(
    (e?: LRUDEvent) => {
      e && e.stopPropagation()
      videoElementRef.current?.playVideo()

      if (isVideoStarted) {
        handleVideoAnalyticsEvent(eventAction.PLAY)
      } else {
        handleVideoAnalyticsEvent(eventAction.START_CLICK)
      }
    },
    [videoElementRef, isVideoStarted, handleVideoAnalyticsEvent],
  )

  const pause = useCallback(
    (e?: LRUDEvent) => {
      e && e.stopPropagation()
      videoElementRef.current?.pauseVideo()
      handleVideoAnalyticsEvent(eventAction.PAUSE)
    },
    [videoElementRef, handleVideoAnalyticsEvent],
  )

  const togglePlayPause = useCallback(() => {
    const playerState = videoElementRef.current?.playerInfo?.playerState
    if (!playerState) {
      return
    }

    return playerState === PlayerState.PLAYING ? pause() : play()
  }, [play, pause, videoElementRef])

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      switch (e.keyCode) {
        case Keys.MEDIA_PLAY:
          return play()
        case Keys.MEDIA_PAUSE:
        case Keys.MEDIA_STOP:
          return pause()
        case Keys.MEDIA_PLAY_PAUSE:
          return togglePlayPause()
      }
    }

    window.addEventListener('keydown', keyDownHandler)
    return () => {
      window.removeEventListener('keydown', keyDownHandler)
    }
  }, [play, pause, togglePlayPause])

  const toggleControlsVisibility = useCallback(
    (next: boolean) => {
      return next ? showVideoControls() : hideVideoControls()
    },
    [showVideoControls, hideVideoControls],
  )

  const options = useMemo(
    () => ({
      onHide: hideVideoControls,
      onShow: showVideoControls,
    }),
    [hideVideoControls, showVideoControls],
  )

  const onSeekBack = () => {
    if (!videoControlsVisible) {
      return
    }

    setPauseTimeUpdate(true)
    return seekBack()
  }
  const onSeekForward = () => {
    if (!videoControlsVisible) {
      return
    }

    setPauseTimeUpdate(true)
    return seekForward()
  }

  const onExit = (e: LRUDEvent) => {
    e.stopPropagation()
    hideVideoControls()
    setYouTubeVideo(null)
  }

  return (
    <StyledControlsContainer
      $inFront={videoControlsVisible}
      $isFullscreen={true}
    >
      <AutoHide
        show={videoControlsVisible}
        onChange={toggleControlsVisibility}
        options={options}
      >
        <ControlsContainer
          defaultFocusChild={1}
          focusId={focusIds.VIDEO_CONTAINER}
          elementType="div"
          orientation={orientation.VERTICAL}
        >
          <AnimationBox
            $height="100%"
            $justifyContent="space-between"
            $showDiv={true}
          >
            <Box $alignItems="center">
              <FocusButtonNode
                elementType="section"
                focusId={'section_2'}
                onSelected={onExit}
              >
                <img src={icons.navigation.backArrow} alt="" />
              </FocusButtonNode>
              <BodyText>{t('pressBack')}</BodyText>
            </Box>

            <div>
              <Box $justifyContent="space-between" $alignItems="center">
                <VideoHeading
                  name={title}
                  description={
                    isDevMode ? description + largeArticleMock : description
                  }
                />
              </Box>
              <Box $marginBottom={20}>
                <div>
                  <FocusButtonNode
                    focusId={'section_1'}
                    elementType="section"
                    onSelected={() => videoControlsVisible && togglePlayPause()}
                    onLeft={onSeekBack}
                    onRight={onSeekForward}
                  >
                    <PlayImage
                      src={playing ? icons.controls.pause : icons.controls.play}
                    />
                  </FocusButtonNode>
                </div>
                {!!duration && (
                  <ProgressBar currentTime={currentTime} duration={duration} />
                )}
              </Box>
            </div>
          </AnimationBox>
        </ControlsContainer>
      </AutoHide>
    </StyledControlsContainer>
  )
}

export const YouTubeControls = memo(YouTubeControlsRaw)
